import { Box, Stack, Typography } from '@mui/material';
import type { GridSortModel } from '@mui/x-data-grid';
import { toast, useIsMobile } from '@side/pantry';
import { format } from 'date-fns';
import { useCallback, useMemo, useRef, useState } from 'react';
import { DataGrid } from '@/components/DataGrid';
import { paymentApi } from '$api-clients/clients';
import { useCompensationReportFilters } from '../CompensationReportFilters/CompensationReportFilters.helpers';
import { ExportToCSVButton } from '../components/ExportToCSVButton';
import {
  createLoadingRows,
  EXPORT_PAGE_SIZE,
  fetchUsersAndTeamsMaps,
  mapDataGridColumnToValueOrEmpty,
  useFilterColumnsByUserRole,
} from '../helpers';
import { useAllTimeStartDate } from '../ReportsConfiguration/useReportsConfiguration';
import { buildCsv, downloadCsv } from '../ReportsExport/ReportsExport.helper';
import { TablePagination } from '../TablePagination/TablePagination';
import {
  buildReportingTransactionsQuery,
  columns,
  compareByCloseDateThenAddress,
  DEFAULT_PAGE_SIZE,
  DealAllocationsTableColumns,
  type DealAllocationsTableRow,
  dealAllocationsDataGroups,
  mapReportingTransactionToRow,
  PAGE_SIZE_OPTIONS,
  type ReportingTransactionResult,
  VISIBILITY_MODEL,
} from './DealAllocationsTable.helpers';
import { useGetDealAllocations } from './hooks/useGetDealAllocations';

interface Props {
  viewReportAs: string;
}

export function DealAllocationsTable({ viewReportAs }: Props) {
  const isMobile = useIsMobile('md');
  const { filters } = useCompensationReportFilters();
  const allTimeStartDate = useAllTimeStartDate();
  const [sortModel, setSortModel] = useState<GridSortModel>([
    { field: DealAllocationsTableColumns.closeDate, sort: 'desc' },
  ]);
  const [columnVisibilityModel, setColumnVisibilityModel] =
    useState<Partial<Record<keyof DealAllocationsTableRow, boolean>>>(
      VISIBILITY_MODEL,
    );
  const [isExporting, setIsExporting] = useState(false);

  const {
    rows,
    loading,
    totalCount,
    rowCount,
    paginationModel,
    setPaginationModel,
  } = useGetDealAllocations({ sortModel, viewReportAs });

  // Keep the last known totalCount stable so the DataGrid doesn't reset to
  // page 0 when data is loading (totalCount is briefly undefined).
  const rowCountRef = useRef(0);
  if (totalCount !== undefined) rowCountRef.current = totalCount;

  const filteredColumns = useFilterColumnsByUserRole({
    columns,
    dataGroups: dealAllocationsDataGroups,
  });
  const mappedColumns = useMemo(() => {
    return filteredColumns.map((column) =>
      mapDataGridColumnToValueOrEmpty(column),
    );
  }, [filteredColumns]);

  const handleSortModelChange = useCallback(
    (model: GridSortModel) => {
      setSortModel(model);
      // Reset to page 0 so the new sort starts from the beginning. React 18
      // batches these two setState calls into a single render, preventing a
      // double fetch.
      setPaginationModel((prev) => ({ ...prev, page: 0 }));
    },
    [setPaginationModel],
  );

  const handleExportCsv = useCallback(async () => {
    setIsExporting(true);
    try {
      const fetchAllPages = async (
        accumulatedResults: ReportingTransactionResult[],
        offset: number,
      ): Promise<ReportingTransactionResult[] | null> => {
        const query = buildReportingTransactionsQuery(
          filters,
          sortModel,
          viewReportAs,
          {
            offset,
            limit: EXPORT_PAGE_SIZE,
          },
          allTimeStartDate,
        );
        const response = await paymentApi.GET('/reporting/transactions', {
          params: { query },
        });

        if (
          response.error !== undefined ||
          !response.data.results ||
          !response.data.totalCount
        ) {
          toast(
            'error',
            'Failed to fetch data. Please try again or contact Side Support for assistance.',
            { showCloseButton: true },
          );
          return null;
        }

        const { results: pageResults, totalCount } = response.data;
        const results = [...accumulatedResults, ...pageResults];

        if (results.length >= totalCount) {
          return results;
        }
        return fetchAllPages(results, offset + pageResults.length);
      };

      const results = await fetchAllPages([], 0);
      if (results === null) return;

      const { usersById, teamsById } = await fetchUsersAndTeamsMaps({
        userIds: results.flatMap((r) =>
          [r.teamMemberIdentityId, r.teamMemberId].filter(
            (id): id is string => !!id,
          ),
        ),
        teamIds: results.flatMap((r) =>
          [r.teamIdentityId].filter((id): id is string => !!id),
        ),
      });
      // Secondary sort by address ASC keeps both sides of the same transaction
      // adjacent. The API only supports a single sort field, so this is applied
      // client-side after fetching all pages.
      const sortedResults = [...results].sort(compareByCloseDateThenAddress);
      const rows: DealAllocationsTableRow[] = sortedResults.map((r) =>
        mapReportingTransactionToRow(r, { usersById, teamsById }),
      );
      const csv = buildCsv(rows, filteredColumns);
      const filename = `Deal-Allocations-${format(new Date(), 'yyyy-MM-dd')}.csv`;
      downloadCsv(csv, filename);
      toast('success', 'Deal Allocations Report exported successfully', {
        showCloseButton: true,
      });
    } catch {
      toast(
        'error',
        'Export failed. Please try again or contact Side Support for assistance.',
        { showCloseButton: true },
      );
    } finally {
      setIsExporting(false);
    }
  }, [viewReportAs, filters, sortModel, filteredColumns, allTimeStartDate]);

  return (
    <Stack sx={{ gap: 2 }} id="deal-allocations-table">
      <Stack
        direction="row"
        sx={{
          justifyContent: 'space-between',
          alignItems: { xs: 'center', md: 'flex-end' },
        }}
      >
        <Stack sx={{ gap: 1 }}>
          <Typography variant={isMobile ? 'subtitle2' : 'subtitle1'}>
            Deal Allocations
          </Typography>
          {isMobile ? null : (
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              Breakdown of deal allocation before payouts are calculated
            </Typography>
          )}
        </Stack>
        <ExportToCSVButton
          onClick={() => void handleExportCsv()}
          loading={isExporting}
          disabled={totalCount === 0}
        />
      </Stack>
      <Box sx={{ position: 'relative' }}>
        <DataGrid
          variant="default"
          columns={mappedColumns}
          disableColumnFilter
          pageSizeOptions={PAGE_SIZE_OPTIONS}
          rows={
            loading
              ? createLoadingRows(paginationModel.pageSize, (i) => ({
                  id: String(i),
                  agent: '',
                  agentAvatar: null,
                  team: '',
                }))
              : rows
          }
          rowCount={rowCountRef.current}
          loading={loading}
          paginationMode="server"
          sortingMode="server"
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          sortModel={sortModel}
          onSortModelChange={handleSortModelChange}
          columnVisibilityModel={columnVisibilityModel}
          onColumnVisibilityModelChange={setColumnVisibilityModel}
          slots={{
            pagination: () => (
              <TablePagination
                variant="compensation"
                hidePaginationControls={
                  !totalCount || !rowCount || totalCount <= DEFAULT_PAGE_SIZE
                }
              />
            ),
            noRowsOverlay: () => (
              <Stack
                direction="column"
                sx={{
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: '100%',
                }}
              >
                <Typography variant="body3" sx={{ color: 'text.secondary' }}>
                  No earnings match the current filters
                </Typography>
              </Stack>
            ),
          }}
          slotProps={{
            columnsManagement: {
              disableShowHideToggle: false,
              disableResetButton: true,
            },
          }}
          sx={{
            ...(totalCount === 0 && {
              '--DataGrid-overlayHeight': '60px',
            }),
            '& .MuiTablePagination-root': {
              justifyContent: 'flex-start',
              display: 'flex',
            },
            '& .MuiToolbar-root': {
              paddingLeft: 0,
            },
          }}
        />
      </Box>
    </Stack>
  );
}
